import React, { Component } from 'react';
import { Button, MenuButton, Menu, MenuItem, Box, Dropdown, Flex } from "monday-ui-react-core";
import { EditableHeading, IconButton } from "monday-ui-react-core";
import { Add, Chart, LongText, Table, TextMedium, Delete } from "monday-ui-react-core/icons";
import mondaySdk from "monday-sdk-js";
import axios from 'axios';
import DOMPurify from 'dompurify';
import { TableDefinitionEdit } from './TableDefinitionEdit';
import { ChartDefinitionEdit } from './ChartDefinitionEdit';
import { TemplateElementTextEdit } from './TemplateElementTextEdit';
import { downloadExistingTemplate } from './fileDownloader';

const monday = mondaySdk();
monday.setApiVersion("2023-10");

export class TemplateBuilder extends Component {
    static displayName = TemplateBuilder.name;

    constructor(props) {
        super(props);

        let firstHeader = {
            id: this.generateUniqueID(),
            elementType: 'Header',
            content: 'Report name',
        };
        if (this.props.dataSource === "SingleItem")
            firstHeader.content = 'Report {{Item.Name}}';

        this.orientations = [
            { value: "Portrait", label: "Portrait" },
            { value: "Landscape", label: "Landscape" }
        ];

        this.state = {
            loading: true,  
            isSaving: false,
            isSaved: false,
            elements: [firstHeader],
            createNewTableDefinition: false,
            createNewChartDefinition: false,
            editText: false,
            editTextId: null,
            selectedElement: null,
            finetuneTemplate: false,
            orientation: "Portrait"
        };
        this.mondayContext = window.mondayContext;
        this.localStorageStateIdentifier = "TemplateBuilder" + this.mondayContext.boardId + "_" + this.mondayContext.boardViewId;
        this.handleClickSave = this.handleClickSave.bind(this);
        this.handleCancelClick = this.handleCancelClick.bind(this);
        this.handleAddText = this.handleAddText.bind(this);
        this.handleAddChart = this.handleAddChart.bind(this);
        this.handleAddHeader = this.handleAddHeader.bind(this);
        this.handleAddTable = this.handleAddTable.bind(this);
        this.handleTableEditClose = this.handleTableEditClose.bind(this);
        this.handleChartEditClose = this.handleChartEditClose.bind(this);
        this.handleHeaderContentChange = this.handleHeaderContentChange.bind(this);
        this.handleElementTextEditClose = this.handleElementTextEditClose.bind(this);
        this.handleFinetuneTemplateCheckboxChange = this.handleFinetuneTemplateCheckboxChange.bind(this);
        this.handleOrientationChange = this.handleOrientationChange.bind(this);
    }

    componentDidMount() {
        try {
            const savedState = localStorage.getItem(this.localStorageStateIdentifier);
            if (savedState) {
                console.log('Using saved state ' + this.localStorageStateIdentifier + ": " + savedState);
                var savedStateParsed = JSON.parse(savedState);
                this.setState(savedStateParsed);
            }
            else
                this.setState({ ...this.state, loading: false });
        } catch (error) {
            console.error("Failed to load TB state from local storage:", error);
        }
    }

    componentDidUpdate(prevProps, prevState) {
        if (this.state.loading)
            return;
        if (this.state.isSaving)
            return;
        if (this.state !== prevState) {
            if (this.state.isSaved)
                localStorage.removeItem(this.localStorageStateIdentifier);
            else
                localStorage.setItem(this.localStorageStateIdentifier, JSON.stringify(this.state));
        }
    }

    generateUniqueID() {
        return 'id-' + Math.random().toString(36).substr(2, 9) + '-' + Date.now();
    }

    handleOrientationChange(orientation) {
        this.setState({ orientation: orientation.value });
    }

    handleAddTable() {
        this.setState({ createNewTableDefinition: true });
    }

    handleTableEditClose(table) {
        if (table) {
            var newElement = {
                id: this.generateUniqueID(),
                elementType: 'Table',
                tableDefinitionId: table.id,
                name: DOMPurify.sanitize(table.name),
            }
            this.setState({ elements: [...this.state.elements, newElement] });
        }
            
        this.setState({ createNewTableDefinition: false });        
    }

    handleAddText() {
        this.setState({ editText: true, selectedElement: null, editTextId: this.generateUniqueID() });
    }

    handleElementTextEditClose(newText) {
        if (newText) {
            if (this.state.selectedElement) {
                const newElements = this.state.elements.map(element => {
                    if (element.id === this.state.selectedElement.id) {
                        return { ...element, content: DOMPurify.sanitize(newText) };
                    }
                    return element;
                });
                this.setState({ elements: newElements });
            }
            else {
                var newElement = {
                    id: this.generateUniqueID(),
                    elementType: 'Text',
                    content: DOMPurify.sanitize(newText)
                }
                this.setState({ elements: [...this.state.elements, newElement] });
            }
        }

        this.setState({ editText: false, selectedElement: null, editTextId: null });        
    }

    handleAddChart() {
        this.setState({ createNewChartDefinition: true });
    }

    handleChartEditClose(chartDefinition) {
        if (chartDefinition) {
            var newElement = {
                id: this.generateUniqueID(),
                elementType: 'Chart',
                chartDefinitionId: chartDefinition.id,
                name: DOMPurify.sanitize(chartDefinition.name),
            }
            this.setState({ elements: [...this.state.elements, newElement] });
        }
        this.setState({ createNewChartDefinition: false });
    }

    handleAddHeader() {
        var newElement = {
            id: this.generateUniqueID(),
            elementType: 'Header',
            content: 'New header',
        }
        this.setState({ elements: [...this.state.elements, newElement] });
    }
    
    handleCancelClick() {
        localStorage.removeItem(this.localStorageStateIdentifier);
        this.props.onClose();
    }

    async handleClickSave() {        
        this.setState({ isSaving: true });
        const data = this.getDataForPOST();
        try {
            const response = await axios.post('templatedefinition', data);
            if (response.data.result) {
                if (this.state.finetuneTemplate) {
                    response.data.template.finetuneTemplate = true;
                    await downloadExistingTemplate(response.data.template, window.mondaySessionToken);                    
                }
                this.setState({ isSaving: false, isSaved: true });
                localStorage.removeItem(this.localStorageStateIdentifier);
                this.props.onClose(response.data.template, response.data.templateDefinition);
            } else {
                var errorMessage = 'Error saving template definition: ' + response.data.error;
                console.log(errorMessage);
                alert(errorMessage);
                this.setState({ isSaving: false });
            }
        } catch (error) {
            console.error('Error:', error);
            alert(error);
            this.setState({ isSaving: false });
        }
    }

    getDataForPOST() {
        const sanitizedElements = this.state.elements.map(element => ({
            ...element,
            content: DOMPurify.sanitize(element.content),
            name: DOMPurify.sanitize(element.name)
        }));
        const data = {            
            accountId: this.mondayContext.account?.id,
            boardId: this.mondayContext.boardId,
            workspaceId: this.mondayContext.workspaceId,
            userCountryCode: this.mondayContext.user.countryCode,
            userLanguage: this.mondayContext.user.currentLanguage,
            sessionToken: window.mondaySessionToken,
            dataSource: DOMPurify.sanitize(this.props.dataSource),
            elements: sanitizedElements,
            orientation: DOMPurify.sanitize(this.state.orientation)
        };
        return data;
    }

    handleFinetuneTemplateCheckboxChange() {
        this.setState({ finetuneTemplate: !this.state.finetuneTemplate });
    }

    cannotSave() {
        return false;
    }

    handleHeaderContentChange = (id, newContent) => {
        const newElements = this.state.elements.map(element => {
            if (element.id === id) {
                return { ...element, content: DOMPurify.sanitize(newContent) };
            }
            return element;
        });

        this.setState({ elements: newElements });
    };

    deleteElement = (e, id) => {
        e.stopPropagation();
        this.setState(prevState => ({
            elements: prevState.elements.filter(element => element.id !== id)
        }));
    };

    handleElementClick = (e, id) => {
        e.stopPropagation();
        const selectedElement = this.state.elements.find(element => element.id === id);
        if (!selectedElement)
            return;
        if (selectedElement.elementType === 'Text')
            this.setState({ selectedElement: selectedElement, editText: true, editTextId: id });
    }

    renderElements() {
        const { elements } = this.state;

        return (
            <div>
                {elements.map((element) => {
                    const { id, elementType, content, name } = element;
                    const boxClassName = `app-spirit-template-builder-element app-spirit-template-builder-element-${elementType}`;
                    let border = (elementType === 'Chart' || elementType === 'Table') ? Box.borders.DEFAULT : null;

                    return (
                        <Box
                            key={id}
                            border={border}
                            className={boxClassName}                            
                        >
                            {this.renderElementContent(elementType, content, name, id)}
                            <IconButton                                
                                size={IconButton.sizes.SMALL}
                                icon={Delete}
                                onClick={(e) => this.deleteElement(e, id)}
                                className="app-spirit-template-builder-delete-button"
                            />
                        </Box>
                    );
                })}
            </div>
        );
    }

    renderElementContent(elementType, content, name, id) {
        switch (elementType) {
            case 'Header':
                return (
                    <EditableHeading
                        value={content}
                        onChange={(text) => this.handleHeaderContentChange(id, text)}
                    />
                );
            case 'Text':
                const sanitizedContent = DOMPurify.sanitize(content);
                return <div onClick={(e) => this.handleElementClick(e, id)} dangerouslySetInnerHTML={{ __html: sanitizedContent }} />;
            case 'Chart':
                return <p>Chart {name}</p>;
            case 'Table':
                return <p>Table {name}</p>;
            default:
                return null;
        }
    }

    render() {
        if (this.state.loading)
            return (<p><em>Loading...</em></p>);

        if (this.state.createNewTableDefinition) {
            return (
                <TableDefinitionEdit
                    columns={this.props.columns}
                    subItemsColumns={this.props.subItemsColumns}
                    dataSource={this.props.dataSource}
                    onClose={this.handleTableEditClose}></TableDefinitionEdit>
            );
        }

        if (this.state.createNewChartDefinition) {
            return (
                <ChartDefinitionEdit onClose={this.handleChartEditClose}></ChartDefinitionEdit>
            );
        }

        if (this.state.editText) {
            return (
                <TemplateElementTextEdit
                    content={this.state.selectedElement?.content}
                    columns={this.props.columns}
                    subItemsColumns={this.props.subItemsColumns}
                    dataSource={this.props.dataSource}
                    isReportGenerator={this.props.isReportGenerator}
                    editTextId={this.state.editTextId}
                    onClose={this.handleElementTextEditClose}></TemplateElementTextEdit>
            );
        }

        let elements = this.renderElements();
        const isSmallScreen = window.innerWidth < 1100;
        let direction = isSmallScreen ? Flex.directions.COLUMN : Flex.directions.ROW;
        let helpCenterUrlTables = this.props.isReportGenerator ? "https://www.pdfreportgenerator.com/help/template/tables/" : "https://www.docexport.com/help/template/tables/";
        let helpCenterUrlCharts = this.props.isReportGenerator ? "https://www.pdfreportgenerator.com/help/template/charts/" : "https://www.docexport.com/help/template/charts/";
        let orientation = this.orientations.find(c => c.value === this.state.orientation);

        return (
            <Flex align={Flex.align.START} direction={direction} gap={32}>
                <div>
                    <Flex className="app-spirit-orientation-flex">
                        <p>Please build your report here:</p>
                        <Dropdown
                            className="app-spirit-dropdown-orientation"
                            size={Dropdown.sizes.SMALL}
                            onBlur={function noRefCheck() { }}
                            onChange={this.handleOrientationChange}
                            onClear={function noRefCheck() { }}
                            onFocus={function noRefCheck() { }}
                            onInputChange={function noRefCheck() { }}
                            onMenuClose={function noRefCheck() { }}
                            onMenuOpen={function noRefCheck() { }}
                            onOptionRemove={function noRefCheck() { }}
                            onOptionSelect={function noRefCheck() { }}
                            openMenuOnFocus={function noRefCheck() { }}
                            value={orientation}
                            options={this.orientations}
                            clearable={false}
                        />
                    </Flex>
                    <Box className="app-spirit-template-builder-box"
                        border={Box.borders.DEFAULT}
                        backgroundColor={Box.backgroundColors.GREY_BACKGROUND_COLOR}
                        rounded={Box.roundeds.MEDIUM}
                        shadow={Box.shadows.MEDIUM}>
                        {elements}
                        <br />
                        <div>
                            <MenuButton
                                text="Add new element"
                                closeMenuOnItemClick
                                className="app-spirit-template-builder-add-button"
                                component={Add}
                                componentPosition={MenuButton.componentPositions.END}
                                onMenuHide={function noRefCheck() { }}
                                onMenuShow={function noRefCheck() { }}>
                                <Menu
                                    id="menu"
                                    size="medium">
                                    <MenuItem
                                        icon={LongText}
                                        onClick={this.handleAddText}
                                        title="Text"
                                    />
                                    <MenuItem
                                        icon={Table}
                                        onClick={this.handleAddTable}
                                        title="Table"
                                    />
                                    <MenuItem
                                        icon={TextMedium}
                                        onClick={this.handleAddHeader}
                                        title="Header"
                                    />
                                    {this.props.dataSource === "Board" && <MenuItem
                                        icon={Chart}
                                        onClick={this.handleAddChart}
                                        title="Chart"
                                    />}
                                </Menu>
                            </MenuButton>
                        </div>
                    </Box>
                    <div>
                        <p></p>
                        <Button loading={this.state.isSaving} disabled={this.cannotSave()} onClick={this.handleClickSave}>
                            Next
                        </Button>
                        <Button kind={Button.kinds.SECONDARY} className="app-spirit-cancelbutton" onClick={this.handleCancelClick}>
                            Cancel
                        </Button>
                    </div>
                </div>
                <div>
                    <Box backgroundColor={Box.backgroundColors.GREY_BACKGROUND_COLOR} border={Box.borders.DEFAULT} padding={Box.paddings.LARGE} rounded={Box.roundeds.MEDIUM}>
                        <p className="app-spirit-explanation-text-top">How does the Report Builder work?</p>
                        <p>Put together your report using various elements:</p>
                        <ol>
                            <li>Add new elements to your report by clicking "Add new element".</li>
                            <li>Select the elements you want to add: Texts, <a href={helpCenterUrlTables} target="_blank" rel="noreferrer">Tables</a>,&nbsp;
                                {this.props.dataSource === "Board" && (<><a href={helpCenterUrlCharts} target="_blank" rel="noreferrer">Charts</a>, </>)}
                                Headers</li>
                            <li>Report Generator then creates a Word template with all the elements you chose. You can download the document as a Word file to make further adjustments, such as changing colors or fonts, by clicking "Download template" after the initial report generation.</li>
                        </ol>
                    </Box>
                </div>
            </Flex>
        );
    }
}